import { API_CONFIG } from '@Web/config/api.config';
import { RestClient } from '@Web/clients/RestClient';
import {
  DEFAULT_GAME_OPERATOR,
  getGameLaunchMeta,
} from '@config/game-launch.config';
import { useAuth } from '@core/gameengine/state/auth';
import { clearGameSession, setGameSession } from '@core/utils/gameSession';
import { isDemoInUrl } from '@core/utils/urlParser';
import { getLanguage } from '@core/i18n';
import { resolveAccessToken } from '@core/utils/resolveAccessToken';

export interface GameLaunchRequest {
  gameName: string;
  category: string;
  mode: 'real' | 'demo';
  language: string;
  device: 'desktop' | 'tablet' | 'mobile';
  operator: string;
}

export interface GameLaunchResponse {
  errorCode: number;
  errorMessage: string;
  playerSessionId: string;
  gameSessionId: string;
  gameId: number;
  category: string;
  provider: string;
  currency: string;
  launchParams: Record<string, unknown>;
}

function getDeviceType(): GameLaunchRequest['device'] {
  if (typeof window === 'undefined') return 'desktop';
  if (window.innerWidth >= 1024) return 'desktop';
  if (window.innerWidth >= 620) return 'tablet';
  return 'mobile';
}

export class GameLaunchService {
  private static instance: GameLaunchService;
  private restClient: RestClient;

  public static getInstance(): GameLaunchService {
    if (!GameLaunchService.instance) {
      GameLaunchService.instance = new GameLaunchService();
    }
    return GameLaunchService.instance;
  }

  private constructor() {
    this.restClient = new RestClient(API_CONFIG.baseUrl);
  }

  public async launchGame(slug: string): Promise<GameLaunchResponse> {
    const meta = getGameLaunchMeta(slug);
    if (!meta) {
      throw new Error(`No game launch configuration for slug: ${slug}`);
    }

    const accessToken = resolveAccessToken();
    if (!accessToken) {
      throw new Error('Cannot launch game without an access token.');
    }

    const payload: GameLaunchRequest = {
      gameName: meta.gameName,
      category: meta.category,
      mode: isDemoInUrl() ? 'demo' : 'real',
      language: getLanguage(),
      device: getDeviceType(),
      operator: DEFAULT_GAME_OPERATOR,
    };

    const path = API_CONFIG.endpoints.gameLaunch(meta.gameId);
    const response = await this.restClient.post<GameLaunchResponse>(
      path,
      payload,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
      }
    );

    if (response.errorCode !== 0) {
      throw new Error(
        response.errorMessage ||
          `Game launch failed (code ${response.errorCode})`
      );
    }

    if (!response.playerSessionId || !response.gameSessionId) {
      throw new Error('Game launch response missing session identifiers.');
    }

    clearGameSession();
    setGameSession({
      playerSessionId: response.playerSessionId,
      gameSessionId: response.gameSessionId,
      gameId: response.gameId,
      currency: response.currency,
      slug,
    });

    useAuth.setState({ playerSessionId: response.playerSessionId });

    return response;
  }
}
